import {
  type Analytics,
  getAnalytics,
  initializeAnalytics,
  logEvent,
  setUserId,
  setUserProperties,
} from "firebase/analytics";
import { firebaseApp } from "./firebase";

let analytics: Analytics | null = null;

function getAnalyticsInstance(): Analytics | null {
  if (analytics) return analytics;
  if (!import.meta.env.VITE_FIREBASE_MEASUREMENT_ID) return null;
  try {
    analytics = initializeAnalytics(firebaseApp, {
      config: { send_page_view: false },
    });
    return analytics;
  } catch {
    // Already initialized (e.g. HMR): fall back to existing instance
    analytics = getAnalytics(firebaseApp);
    return analytics;
  }
}

export function trackScreenView(screenName: string): void {
  const a = getAnalyticsInstance();
  if (!a) return;
  logEvent(a, "page_view", { page_title: screenName });
}

export function trackLogin(method: string): void {
  const a = getAnalyticsInstance();
  if (!a) return;
  logEvent(a, "login", { method });
}

export function trackSignUp(method: string): void {
  const a = getAnalyticsInstance();
  if (!a) return;
  logEvent(a, "sign_up", { method });
}

export function trackLogout(): void {
  const a = getAnalyticsInstance();
  if (!a) return;
  logEvent(a, "logout");
}

export function trackItemCreate(params: {
  item_name: string;
  interval_days: number;
  from_template: boolean;
}): void {
  const a = getAnalyticsInstance();
  if (!a) return;
  logEvent(a, "item_create", {
    ...params,
    from_template: params.from_template ? 1 : 0,
  });
}

export function trackItemEdit(itemId: string): void {
  const a = getAnalyticsInstance();
  if (!a) return;
  logEvent(a, "item_edit", { item_id: itemId });
}

export function trackItemDelete(itemId: string): void {
  const a = getAnalyticsInstance();
  if (!a) return;
  logEvent(a, "item_delete", { item_id: itemId });
}

export function trackItemPurchase(itemId: string): void {
  const a = getAnalyticsInstance();
  if (!a) return;
  logEvent(a, "item_purchase", { item_id: itemId });
}

export function trackGroupCreate(groupId: string): void {
  const a = getAnalyticsInstance();
  if (!a) return;
  logEvent(a, "group_create", { group_id: groupId });
}

export function trackGroupJoin(): void {
  const a = getAnalyticsInstance();
  if (!a) return;
  logEvent(a, "group_join");
}

export function trackGroupLeave(groupId: string): void {
  const a = getAnalyticsInstance();
  if (!a) return;
  logEvent(a, "group_leave", { group_id: groupId });
}

export function trackGroupSwitch(groupId: string): void {
  const a = getAnalyticsInstance();
  if (!a) return;
  logEvent(a, "group_switch", { group_id: groupId });
}

export function trackPaywallView(): void {
  const a = getAnalyticsInstance();
  if (!a) return;
  logEvent(a, "paywall_view");
}

export function trackWaitlistJoin(): void {
  const a = getAnalyticsInstance();
  if (!a) return;
  logEvent(a, "waitlist_join");
}

export function trackWaitlistLeave(): void {
  const a = getAnalyticsInstance();
  if (!a) return;
  logEvent(a, "waitlist_leave");
}

export function setAnalyticsUserId(userId: string): void {
  const a = getAnalyticsInstance();
  if (!a) return;
  setUserId(a, userId);
}

export function clearAnalyticsUserId(): void {
  const a = getAnalyticsInstance();
  if (!a) return;
  setUserId(a, null);
}

export function setAnalyticsLanguage(language: string): void {
  const a = getAnalyticsInstance();
  if (!a) return;
  setUserProperties(a, { app_language: language });
}

export function trackLanguageChange(language: string): void {
  const a = getAnalyticsInstance();
  if (!a) return;
  logEvent(a, "language_change", { language });
}
